// https://fkhadra.github.io/react-toastify/introduction/
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect } from 'react';
import Form from './ui/form/Form';
import DownloadButton from './ui/buttons/DownloadButton';
import { rootActions, useAppDispatch, useAppSelector } from './data/store';
import { getIconByKey, getRandomIcon, ICONS_SIZES } from './domain/icons-provider';
import IconPreview from './ui/IconPreview';
import SaveForLaterButton from './ui/buttons/SaveForLaterButton';
import RandomizeButton from './ui/buttons/RandomizeButton';
import SavedIcons from './ui/saved-icons/SavedIcons';
import Terminal from './ui/Terminal';
import TablerCategories from './ui/categories/TablerCategories';
import SocialShare from './ui/social-share/SocialShare';
import { restoreIconDropDeepLink } from './domain/deep-link-provider';
import { IIcon } from './interfaces';
import { getIconsData } from './data/data-provider';
import ConsentBanner from './ui/gdpr/ConsentBanner';
import SearchIconButton from './ui/buttons/SearchIconButton';
import SearchIconsPopup from './ui/search-icons/SearchIconsPopup';

/**
 * SVG Stripes Maker.
 */
const App = () => {

    const dispatch = useAppDispatch();

    const iconsData = useAppSelector(store => store.root.iconsData);
    const savedForLaterIcons = useAppSelector(store => store.root.savedForLaterIcons);
    const randomIcon = useAppSelector(store => store.root.randomIcon);
    const category = useAppSelector(store => store.root.category);
    const searchIconPopupShown = useAppSelector(store => store.root.searchIconPopupShown);

    // form properties from redux store
    const strokeWidth = useAppSelector(store => store.form.strokeWidth);
    const strokeColor = useAppSelector(store => store.form.strokeColor);
    const hasStroke = useAppSelector(store => store.form.hasStroke);

    const fillColor = useAppSelector(store => store.form.fillColor);
    const hasFill = useAppSelector(store => store.form.hasFill);

    const backgroundColor = useAppSelector(store => store.form.backgroundColor);
    const backgroundType = useAppSelector(store => store.form.backgroundType);

    const rotation = useAppSelector(store => store.form.rotation);
    const scale = useAppSelector(store => store.form.scale);

    const sharpness = useAppSelector(store => store.form.sharpness);
    const smoothness = useAppSelector(store => store.form.smoothness);

    /**
     * set initial random icon
     */
    useEffect(() => {
        (async () => {
            const restoredIcon = restoreIconDropDeepLink();

            let updatedIcon: IIcon|null = null;

            if(restoredIcon){
                updatedIcon = await getIconByKey(getIconsData(), restoredIcon);
            }

            if(!updatedIcon){
                updatedIcon = await getRandomIcon(iconsData, category);
            }

            dispatch(
              rootActions.main({
                  randomIcon: updatedIcon,
              })
            );
        })();
    }, [dispatch, iconsData, category]);

    const setSearchIconPopupShown = (isShown: boolean) => {
        dispatch(
            rootActions.main({
                searchIconPopupShown: isShown,
            })
        );
    };

    return (
        <>
            <div className="flex-1">

                <div className="hidden xl:block">
                    <TablerCategories />
                </div>

                <section className="bg-gray-800 text-gray-300 py-10 mb-10 shadow-xl">
                    <div className="container mx-auto my-8 px-4">
                        <div className="flex flex-col xl:flex-row gap-8 mb-8">

                            <div className="flex-1 order-2 xlg:order-1">
                                <Form />
                            </div>

                            <div id="preview" className="flex flex-col items-center order-1 xl:order-2">
                                <div className="preview bg-gray-200 overflow-hidden max-w-full p-4 flex items-center justify-center rounded-xl">
                                    <div className="grid grid-cols-4 gap-4">
                                        {
                                            randomIcon &&
                                            <>
                                                {
                                                    ICONS_SIZES.map(iconSize => {
                                                        return (
                                                            <IconPreview
                                                                key={ `icon-preview-${ iconSize }` }
                                                                iconSvg={ randomIcon.svg }
                                                                size={ iconSize }

                                                                strokeWidth={ strokeWidth }
                                                                strokeColor={ strokeColor }
                                                                hasStroke={ hasStroke }

                                                                fillColor={ fillColor }
                                                                hasFill={ hasFill }

                                                                backgroundColor={ backgroundColor }
                                                                backgroundType={ backgroundType }

                                                                rotation={ rotation }
                                                                scale={ scale }

                                                                sharpness={ sharpness }
                                                                smoothness={ smoothness }
                                                            />
                                                        )
                                                    })
                                                }

                                                {
                                                    ICONS_SIZES.map(iconSize => {
                                                        return (
                                                            <div
                                                                key={ `icon-preview-size-${ iconSize }` }
                                                                className="bg-white rounded py-1 px-4 text-center flex justify-center items-center justify-self-center text-sm text-gray-900"
                                                            >
                                                                { iconSize }
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </>
                                        }
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4 mb-4 mt-10">
                                    <RandomizeButton />
                                    <SearchIconButton />
                                    <DownloadButton />
                                    <SaveForLaterButton />
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                <div className="xl:hidden">
                    <TablerCategories />
                </div>

                <section className="container mx-auto my-8 px-4">

                    { savedForLaterIcons && savedForLaterIcons.length > 0 && <SavedIcons /> }

                    <div id="instructions" className="grid lg:grid-cols-2 gap-8">
                        <Terminal title="HTML" content={ `
<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
<link rel="icon" type="image/png" sizes="48x48" href="/favicon-48.png" />
<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32.png">
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16.png">
<link rel="manifest" href="/manifest.json">
<meta name="msapplication-TileColor" content="#da532c">
<meta name="theme-color" content="#ffffff">
                    ` }/>

                       <Terminal textOnly={ true } title="Instructions" content={ `
- Click on "Randomize" button till you find the best icon for your website.
- Use the category filters to narrow down the icons set.
- Adjust icon style by choosing colors, stroke width, etc.
- Click on the "Download" button and get a zip archive with all the images.
- Extract this package in the root of your web site.
- Copy the HTML code to the HEAD section of your pages.
- Have fun! 😎                   
                       ` }/>
                    </div>
                </section>

                <SocialShare />
            </div>

            { searchIconPopupShown && <SearchIconsPopup setPopupShown={ setSearchIconPopupShown } /> }

            {/* https://fkhadra.github.io/react-toastify/introduction/ */}
            <ToastContainer
                autoClose={ 2000 }
                position="top-right"
            />

            <ConsentBanner />
        </>
    )
};

export default App;
